import { Brain, FileUser, Handshake, type LucideIcon, Megaphone, Radar, ReceiptEuro } from 'lucide-react';
import { hasModuleAccess } from '@/lib/module-permissions';
import type { HubApp } from '../types';

/**
 * Hub Apps Configuration
 *
 * Module gate permissions follow {module}:view (agent) / {module}:admin (admin)
 * and are synced via infra/workos/rbac/.
 */

export interface AppConfig {
  id: string;
  name: string;
  description: string;
  icon: LucideIcon;
  route: string;
  /** Hub module slug for view/admin gate permissions. */
  module: string;
  comingSoon: boolean;
}

export const APPS_CONFIG: AppConfig[] = [
  {
    id: 'app_customers',
    name: 'Customer Care',
    description: 'Manage customer relationships and communications across all channels',
    icon: Handshake,
    route: '/customers',
    module: 'customer-care',
    comingSoon: false,
  },
  {
    id: 'app_campaigns',
    name: 'Campaigns',
    description: 'Design and schedule campaign templates across all your channels',
    icon: Megaphone,
    route: '/campaigns',
    module: 'campaigns',
    comingSoon: false,
  },
  {
    id: 'app_ai',
    name: 'AI Agents',
    description: 'Configure and speak to your AI agents',
    icon: Brain,
    route: '/ai',
    module: 'ai-agents',
    comingSoon: false,
  },
  {
    id: 'app_ingestion',
    name: 'Data Ingestion',
    description: 'Track and manage all data imports and exports',
    icon: FileUser,
    route: '/ingestion',
    module: 'ingestion',
    comingSoon: false,
  },
  {
    id: 'app_finances',
    name: 'Finances',
    description: 'View ledgers, transactions, and financial reconciliation',
    icon: ReceiptEuro,
    route: '/finances',
    module: 'finances',
    comingSoon: false,
  },
  {
    id: 'app_analytics',
    name: 'Analytics',
    description: 'Dashboards, performance and engagement metrics, data exports, and report automations',
    icon: Radar,
    route: '/analytics',
    module: 'analytics',
    comingSoon: false,
  },
];

// Export as HubApp array for backward compatibility
export const hubApps: HubApp[] = APPS_CONFIG.map((app) => ({
  id: app.id,
  name: app.name,
  label: app.name,
  icon: app.icon,
  description: app.description,
  href: app.route,
  route: app.route,
  comingSoon: app.comingSoon,
}));

export function getAppById(id: string): AppConfig | undefined {
  return APPS_CONFIG.find((app) => app.id === id);
}

export function getAppsByPermissions(hasPermission: (permission: string) => boolean): AppConfig[] {
  return APPS_CONFIG.filter((app) => hasModuleAccess(hasPermission, app.module));
}
